import { useEffect } from 'react';
import './TricklightAnimation.css';

/**
 * PROFESSIONAL TRICKLIGHT ANIMATION
 * Electric-type special move with misdirection and lightning effects
 * 
 * Key Features:
 * - Electric-type special attack (80 base power, 100% accuracy)
 * - Creates illusory electric orbs that trick and disorient the opponent
 * - Has a 30% chance to paralyze the target
 * - Professional gaming company quality with lightning particle effects
 * - Special move animation with misdirection and convergence mechanics
 */
export default function TricklightAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Charging Light Orbs (0-600ms)
    console.log('Tricklight Phase 1: Charging Light Orbs');
    
    // Player charging effect
    playerEl.classList.add('tricklight-user-charging');
    
    // Create charging aura around player
    const chargingAura = document.createElement('div');
    chargingAura.className = 'tricklight-charging-aura';
    chargingAura.style.left = `${playerX - 80}px`;
    chargingAura.style.top = `${playerY - 80}px`;
    chargingAura.style.width = '160px';
    chargingAura.style.height = '160px';
    container.appendChild(chargingAura);
    elements.push(chargingAura);

    // Generate initial electric orbs around player
    const orbColors = ['#FFFF00', '#FFF673', '#FFFAAD', '#F7D358', '#FFCC00'];
    for (let i = 0; i < 8; i++) {
      const lightOrb = document.createElement('div');
      lightOrb.className = 'tricklight-orb tricklight-charging-orb';
      const angle = (i / 8) * 360;
      const radius = 60;
      const orbX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const orbY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      lightOrb.style.left = `${orbX - 15}px`;
      lightOrb.style.top = `${orbY - 15}px`;
      lightOrb.style.backgroundColor = orbColors[i % orbColors.length];
      lightOrb.style.animationDelay = `${i * 70}ms`;
      container.appendChild(lightOrb);
      elements.push(lightOrb);
    }

    // PHASE 2: Misdirection (600-1200ms)
    const phase2Timer = setTimeout(() => {
      console.log('Tricklight Phase 2: Misdirection');
      
      // Remove charging effect
      playerEl.classList.remove('tricklight-user-charging');
      playerEl.classList.add('tricklight-user-casting');
      
      // Launch multiple decoy orbs in different directions
      for (let i = 0; i < 12; i++) {
        const decoyOrb = document.createElement('div');
        decoyOrb.className = 'tricklight-orb tricklight-decoy-orb';
        
        // Random starting position near player
        const startAngle = (i / 12) * 360;
        const startRadius = 40 + (i % 3) * 10;
        const startX = playerX + Math.cos(startAngle * Math.PI / 180) * startRadius;
        const startY = playerY + Math.sin(startAngle * Math.PI / 180) * startRadius;
        
        // Random ending position (not directly at opponent)
        const randomAngle = Math.random() * 360;
        const randomDistance = 150 + Math.random() * 150;
        const endX = opponentX + Math.cos(randomAngle * Math.PI / 180) * randomDistance;
        const endY = opponentY + Math.sin(randomAngle * Math.PI / 180) * randomDistance;
        
        // Set position and animation properties
        decoyOrb.style.left = `${startX - 15}px`;
        decoyOrb.style.top = `${startY - 15}px`;
        decoyOrb.style.backgroundColor = orbColors[i % orbColors.length];
        decoyOrb.style.setProperty('--end-x', `${endX - startX}px`);
        decoyOrb.style.setProperty('--end-y', `${endY - startY}px`);
        decoyOrb.style.animationDelay = `${i * 50}ms`;
        
        container.appendChild(decoyOrb);
        elements.push(decoyOrb);
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Convergence Attack (1200-1700ms)
    const phase3Timer = setTimeout(() => {
      console.log('Tricklight Phase 3: Convergence Attack');
      
      // Remove casting effect
      playerEl.classList.remove('tricklight-user-casting');
      
      // Create true attack orbs that converge on opponent
      for (let i = 0; i < 8; i++) {
        const attackOrb = document.createElement('div');
        attackOrb.className = 'tricklight-orb tricklight-attack-orb';
        
        // Random starting position around the battle area
        const randomAngle = Math.random() * 360;
        const randomRadius = 150 + Math.random() * 100;
        const startX = opponentX + Math.cos(randomAngle * Math.PI / 180) * randomRadius;
        const startY = opponentY + Math.sin(randomAngle * Math.PI / 180) * randomRadius;
        
        // Set position and animation properties
        attackOrb.style.left = `${startX - 15}px`;
        attackOrb.style.top = `${startY - 15}px`;
        attackOrb.style.backgroundColor = orbColors[i % orbColors.length];
        attackOrb.style.setProperty('--end-x', `${opponentX - startX}px`);
        attackOrb.style.setProperty('--end-y', `${opponentY - startY}px`);
        attackOrb.style.animationDelay = `${i * 50}ms`;
        
        container.appendChild(attackOrb);
        elements.push(attackOrb);
      }
      
      // Add a slight delay before impact
      const impactTimer = setTimeout(() => {
        // Opponent hit reaction
        opponentEl.classList.add('tricklight-opponent-hit');
        
        // Create impact explosion
        const impactExplosion = document.createElement('div');
        impactExplosion.className = 'tricklight-impact-explosion';
        impactExplosion.style.left = `${opponentX - 100}px`;
        impactExplosion.style.top = `${opponentY - 100}px`;
        impactExplosion.style.width = '200px';
        impactExplosion.style.height = '200px';
        container.appendChild(impactExplosion);
        elements.push(impactExplosion);
        
        // Create paralysis effect (30% chance to paralyze)
        for (let i = 0; i < 6; i++) {
          const paralysisSpark = document.createElement('div');
          paralysisSpark.className = 'tricklight-paralysis-spark';
          paralysisSpark.textContent = '';
          
          const starAngle = (i / 6) * 360;
          const starRadius = 40;
          const starX = opponentX + Math.cos(starAngle * Math.PI / 180) * starRadius;
          const starY = opponentY + Math.sin(starAngle * Math.PI / 180) * starRadius;
          
          paralysisSpark.style.left = `${starX - 10}px`;
          paralysisSpark.style.top = `${starY - 10}px`;
          paralysisSpark.style.animationDelay = `${i * 100}ms`;
          
          container.appendChild(paralysisSpark);
          elements.push(paralysisSpark);
        }
      }, 300);
      elements.push({ timer: impactTimer, type: 'timer' });
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1900ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Tricklight: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('tricklight-user-charging', 'tricklight-user-casting');
      opponentEl.classList.remove('tricklight-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1900);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('tricklight-user-charging', 'tricklight-user-casting');
      opponentEl.classList.remove('tricklight-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
TricklightAnimation.moveType = 'electric';
